/**
 * Store Zustand - Heartbeats
 * Gestion d'état centralisée pour l'application
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ============================================
// STORE UTILISATEUR
// ============================================
export const useUserStore = create(
  persist(
    (set, get) => ({
      // État
      customer: null,
      isLoggedIn: false,
      isDarkMode: true,
      
      // Actions
      setCustomer: (customer) => set({ customer, isLoggedIn: !!customer }),
      logout: () => set({ customer: null, isLoggedIn: false }),
      toggleDarkMode: () => set((state) => ({ isDarkMode: !state.isDarkMode })),
      setDarkMode: (isDarkMode) => set({ isDarkMode }),
      
      // Mise à jour partielle du customer
      updateCustomer: (updates) => set((state) => ({
        customer: state.customer ? { ...state.customer, ...updates } : null
      })),
    }),
    {
      name: 'lpe-user-storage',
      partialize: (state) => ({ 
        customer: state.customer, 
        isLoggedIn: state.isLoggedIn,
        isDarkMode: state.isDarkMode 
      }),
    }
  )
);


// ============================================
// STORE FLAMME (Rencontres)
// ============================================
export const useHeartbeatsStore = create((set, get) => ({
  // État
  profile: null,
  matches: [],
  crushes: [],
  receivedCrushes: [],
  onlineUsers: [],
  crowns: 0,
  rank: null,
  
  // Profils à découvrir
  discoverProfiles: [],
  currentProfileIndex: 0,
  
  // Chat
  activeMatchId: null,
  messages: {},  // { matchId: [messages] }
  typingUsers: {},  // { matchId: { customerId: true } }
  unreadCounts: {},  // { matchId: count }
  
  // Appels
  incomingCall: null,
  activeCall: null,
  
  // UI State
  activeTab: 'swipe',
  showChat: false,
  
  // Actions - Profil
  setProfile: (profile) => set({ profile }),
  updateProfile: (updates) => set((state) => ({
    profile: state.profile ? { ...state.profile, ...updates } : null
  })),
  
  // Actions - Matchs
  setMatches: (matches) => set({ matches }),
  addMatch: (match) => set((state) => ({
    matches: [match, ...state.matches]
  })),
  removeMatch: (matchId) => set((state) => ({
    matches: state.matches.filter(m => m.id !== matchId)
  })),
  
  // Actions - Crushes
  setCrushes: (crushes) => set({ crushes }),
  setReceivedCrushes: (receivedCrushes) => set({ receivedCrushes }),
  addCrush: (crush) => set((state) => ({
    crushes: [crush, ...state.crushes]
  })),
  
  // Actions - Découverte
  setDiscoverProfiles: (profiles) => set({ 
    discoverProfiles: profiles,
    currentProfileIndex: 0 
  }),
  nextProfile: () => set((state) => ({
    currentProfileIndex: Math.min(state.currentProfileIndex + 1, state.discoverProfiles.length - 1)
  })),
  getCurrentProfile: () => {
    const state = get();
    return state.discoverProfiles[state.currentProfileIndex] || null;
  },
  
  // Actions - Couronnes
  setCrowns: (crowns, rank = null) => set({ crowns, rank }),
  addCrowns: (amount) => set((state) => ({ crowns: state.crowns + amount })),
  deductCrowns: (amount) => set((state) => ({ crowns: Math.max(0, state.crowns - amount) })),
  
  // Actions - Online
  setOnlineUsers: (users) => set({ onlineUsers: users }),
  addOnlineUser: (userId) => set((state) => ({
    onlineUsers: [...new Set([...state.onlineUsers, userId])]
  })),
  removeOnlineUser: (userId) => set((state) => ({
    onlineUsers: state.onlineUsers.filter(id => id !== userId)
  })),
  
  // Actions - Chat
  setActiveMatch: (matchId) => set({ activeMatchId: matchId, showChat: !!matchId }),
  closeChat: () => set({ activeMatchId: null, showChat: false }),
  
  setMessages: (matchId, messages) => set((state) => ({
    messages: { ...state.messages, [matchId]: messages }
  })),
  addMessage: (matchId, message) => set((state) => ({
    messages: {
      ...state.messages,
      [matchId]: [...(state.messages[matchId] || []), message]
    }
  })),
  updateMessage: (matchId, messageId, updates) => set((state) => ({
    messages: {
      ...state.messages,
      [matchId]: (state.messages[matchId] || []).map(m =>
        m.id === messageId ? { ...m, ...updates } : m
      )
    }
  })),
  deleteMessage: (matchId, messageId) => set((state) => ({
    messages: {
      ...state.messages,
      [matchId]: (state.messages[matchId] || []).filter(m => m.id !== messageId)
    }
  })),
  
  // Actions - Typing
  setTyping: (matchId, customerId, isTyping) => set((state) => ({
    typingUsers: {
      ...state.typingUsers,
      [matchId]: isTyping
        ? { ...(state.typingUsers[matchId] || {}), [customerId]: true }
        : Object.fromEntries(
            Object.entries(state.typingUsers[matchId] || {}).filter(([k]) => k !== customerId)
          )
    }
  })),
  
  // Actions - Unread
  incrementUnread: (matchId) => set((state) => ({
    unreadCounts: {
      ...state.unreadCounts,
      [matchId]: (state.unreadCounts[matchId] || 0) + 1
    }
  })),
  clearUnread: (matchId) => set((state) => ({
    unreadCounts: { ...state.unreadCounts, [matchId]: 0 }
  })),
  
  // Actions - Appels
  setIncomingCall: (call) => set({ incomingCall: call }),
  clearIncomingCall: () => set({ incomingCall: null }),
  setActiveCall: (call) => set({ activeCall: call }),
  clearActiveCall: () => set({ activeCall: null }),
  
  // Actions - UI
  setActiveTab: (tab) => set({ activeTab: tab }),
  
  // Reset
  reset: () => set({
    profile: null,
    matches: [],
    crushes: [],
    receivedCrushes: [],
    onlineUsers: [],
    crowns: 0,
    rank: null,
    discoverProfiles: [],
    currentProfileIndex: 0,
    activeMatchId: null,
    messages: {},
    typingUsers: {},
    unreadCounts: {},
    incomingCall: null,
    activeCall: null,
    activeTab: 'swipe',
    showChat: false,
  }),
}));


// ============================================
// STORE NOTIFICATIONS
// ============================================
export const useNotificationStore = create((set, get) => ({
  notifications: [],
  unreadCount: 0,
  
  addNotification: (notification) => set((state) => ({
    notifications: [
      { id: Date.now(), createdAt: new Date().toISOString(), read: false, ...notification },
      ...state.notifications
    ].slice(0, 50),  // Garder max 50 notifications
    unreadCount: state.unreadCount + 1
  })),
  
  markAsRead: (notificationId) => set((state) => ({
    notifications: state.notifications.map(n =>
      n.id === notificationId ? { ...n, read: true } : n
    ),
    unreadCount: Math.max(0, state.unreadCount - 1)
  })),
  
  markAllAsRead: () => set((state) => ({
    notifications: state.notifications.map(n => ({ ...n, read: true })),
    unreadCount: 0
  })),
  
  clearNotifications: () => set({ notifications: [], unreadCount: 0 }),
}));


// ============================================
// STORE UI GLOBAL
// ============================================
export const useUIStore = create((set) => ({
  // Modals
  modals: {
    settings: false,
    profile: false,
    gift: null,  // { targetId, targetName }
    superLike: null,  // { targetId, targetName, targetAvatar }
    verification: false,
    videoCall: null,  // { matchId, partnerName, partnerAvatar }
  },
  
  // Loading states
  loading: {
    global: false,
    profiles: false,
    messages: false,
    matches: false,
  },
  
  // Actions - Modals
  openModal: (modalName, data = true) => set((state) => ({
    modals: { ...state.modals, [modalName]: data }
  })),
  closeModal: (modalName) => set((state) => ({
    modals: { ...state.modals, [modalName]: false }
  })),
  closeAllModals: () => set((state) => ({
    modals: Object.fromEntries(Object.keys(state.modals).map(k => [k, false]))
  })),
  
  // Actions - Loading
  setLoading: (key, value) => set((state) => ({
    loading: { ...state.loading, [key]: value }
  })),
}));


// ============================================
// HOOKS COMBINÉS (Selectors)
// ============================================

// Hook pour obtenir le profil courant à swiper
export const useCurrentSwipeProfile = () => {
  return useHeartbeatsStore((state) => 
    state.discoverProfiles[state.currentProfileIndex] || null
  );
};

// Hook pour obtenir les messages d'un match
export const useMatchMessages = (matchId) => {
  return useHeartbeatsStore((state) => state.messages[matchId] || []);
};

// Hook pour savoir si quelqu'un tape dans un match
export const useIsTyping = (matchId, excludeCustomerId) => {
  return useHeartbeatsStore((state) => {
    const typing = state.typingUsers[matchId] || {};
    return Object.keys(typing).filter(id => id !== excludeCustomerId).length > 0;
  });
};

// Hook pour le nombre total de messages non lus
export const useTotalUnread = () => {
  return useHeartbeatsStore((state) => 
    Object.values(state.unreadCounts).reduce((sum, count) => sum + count, 0)
  );
};

export default {
  useUserStore,
  useHeartbeatsStore,
  useNotificationStore,
  useUIStore,
};
